/**
 * The in-page session: the samples of one recording (180 s), and the archive the
 * backend accepts, built in the browser and verified before it is sent.
 *
 * This is the producer side of the contract `brainspot-backend/brainspot/archive.py`
 * enforces on upload, ported from `calibrations/backend/export.py` (Sprint 1.9.4)
 * with the same two rules:
 *
 *   Refuse, never repair. `inspect()` collects EVERY problem it can find and the
 *   builder returns them all; nothing re-dates or rewrites anything. A refusal
 *   is a fact about the recording, shown on a "kept, not processed" screen.
 *
 *   Verify before sending. The archive is reopened (gunzip, tar walk, parquet
 *   footer) and must say what the builder says, or it is refused as
 *   SELF_CHECK_FAILED.
 *
 * Members, in this order: `meta.json`, `signal.parquet`, `journal.json` (the
 * last only when the journal was answered — a skipped journal writes no file,
 * so the archive has two members and `meta.json` says `journal_status: skipped`).
 *
 * `signal.parquet` columns: `pack_num` int64, `wallclock_ns` int64, one
 * `<Ch>_uV` float32 per hardware channel, `EOG_uV` float32 = mean of Fp1/Fp2 on a
 * Pro or T3/T4 on a classic unit. Row cap 60,000 (240 s at 250 Hz).
 *
 * `session_id` is minted from the recording's ACTUAL start as
 * `session_YYYY-MM-DDTHH-MM-SS-ffffff`, and `start_wallclock_iso` is the same
 * instant with its offset, both rendered in the same (local) clock so the
 * backend's naive comparison sees them agree.
 */

import { parquetMetadata } from 'hyparquet'
import { parquetWriteBuffer } from 'hyparquet-writer'

import { DEVICE_LABELS } from '../devices'
import type { HeadbandInfo, Sample } from '../headband/types'
import { RECORD_SECONDS, SAMPLING_RATE } from './quality'
import type { FinishReport, Refusal } from './phase'

/* ── constants mirrored from the backend and export.py ─────────────────── */

export const MAX_SIGNAL_ROWS = 60_000
export const MAX_ARCHIVE_BYTES = 64 * 1024 * 1024
export const EXPECTED_SAMPLING_RATE = SAMPLING_RATE
export const CHANNEL_RENAME: Readonly<Record<string, string>> = { T3: 'T7', T4: 'T8', T5: 'P7', T6: 'P8' }
export const KNOWN_LAYOUTS: Readonly<Record<string, readonly string[]>> = {
  brainbit_pro: ['Fp1', 'Fp2', 'T7', 'T8', 'P7', 'P8', 'O1', 'O2'],
  brainbit: ['T7', 'T8', 'O1', 'O2'],
}
/** The headband's name and contact count, from the ONE device mapping (`lib/devices.ts`). */
export const LAYOUT_LABELS: Readonly<Record<string, string>> = DEVICE_LABELS
export const CANONICAL_SESSION_ID = /^session_(\d{4})-(\d{2})-(\d{2})T(\d{2})-(\d{2})-(\d{2})-(\d{6})$/
export const MAX_ACQUISITION_DRIFT_S = 1.0
export const CLEAN_FINALIZE_REASON = 'finish_session'
export const JOURNAL_SCHEMA_VERSION = 1
/** Under this many complete seconds the recording is an incomplete attempt. */
export const MIN_COMPLETE_SECONDS = RECORD_SECONDS

export type RefusalCode =
  | 'INCOMPLETE'
  | 'NO_SIGNAL_ROWS'
  | 'TOO_MANY_ROWS'
  | 'UNKNOWN_LAYOUT'
  | 'META_BAD_SAMPLING_RATE'
  | 'SESSION_ID_NOT_CANONICAL'
  | 'ID_TIMESTAMP_DISAGREE'
  | 'NEVER_ENDED'
  | 'PARTIAL'
  | 'NOT_FINISHED_CLEANLY'
  | 'ARCHIVE_TOO_LARGE'
  | 'SELF_CHECK_FAILED'

/* ── time ───────────────────────────────────────────────────────────────── */

export interface Instant {
  /** Unix microseconds. */
  us: number
  /** Minutes east of UTC (JS `getTimezoneOffset()` is the negative of this). */
  offsetMinutes: number
}

export function instantNow(): Instant {
  const ms = Date.now()
  const frac = typeof performance !== 'undefined' ? (performance.now() * 1000) % 1000 : 0
  return { us: ms * 1000 + Math.floor(frac), offsetMinutes: -new Date(ms).getTimezoneOffset() }
}

interface LocalParts {
  y: number
  mo: number
  d: number
  h: number
  mi: number
  s: number
  us: number
}

function localParts(at: Instant): LocalParts {
  // Render the instant in the recorder's own offset, the way Python's
  // `datetime.now(tz)` would, so id and ISO stamp agree naively.
  const shifted = new Date(Math.floor(at.us / 1000) + at.offsetMinutes * 60_000)
  return {
    y: shifted.getUTCFullYear(),
    mo: shifted.getUTCMonth() + 1,
    d: shifted.getUTCDate(),
    h: shifted.getUTCHours(),
    mi: shifted.getUTCMinutes(),
    s: shifted.getUTCSeconds(),
    us: ((at.us % 1_000_000) + 1_000_000) % 1_000_000,
  }
}

const p2 = (n: number) => String(n).padStart(2, '0')

/** `session_YYYY-MM-DDTHH-MM-SS-ffffff`, in the recorder's local clock. */
export function mintSessionId(at: Instant): string {
  const t = localParts(at)
  return `session_${t.y}-${p2(t.mo)}-${p2(t.d)}T${p2(t.h)}-${p2(t.mi)}-${p2(t.s)}-${String(t.us).padStart(6, '0')}`
}

/** `YYYY-MM-DDTHH:MM:SS.ffffff±HH:MM`, the same instant with its offset. */
export function isoWithOffset(at: Instant): string {
  const t = localParts(at)
  const sign = at.offsetMinutes < 0 ? '-' : '+'
  const abs = Math.abs(at.offsetMinutes)
  return `${t.y}-${p2(t.mo)}-${p2(t.d)}T${p2(t.h)}:${p2(t.mi)}:${p2(t.s)}.${String(t.us).padStart(6, '0')}${sign}${p2(Math.floor(abs / 60))}:${p2(abs % 60)}`
}

/* ── the recorder ───────────────────────────────────────────────────────── */

export interface Recording {
  startedAt: Instant
  endedAt: Instant
  channels: readonly string[]
  /** Row-major: one array per sample, values in `channels` order. */
  samples: Sample[]
  finalizeReason: string
  partial: boolean
}

/** Collects the samples of one attempt. Stop marks how it ended. */
export class SessionRecorder {
  private startedAt: Instant | null = null
  private endedAt: Instant | null = null
  private readonly samples: Sample[] = []
  private reason: string | null = null
  readonly channels: readonly string[]

  constructor(channels: readonly string[]) {
    this.channels = channels
  }

  start(at: Instant = instantNow()): void {
    this.startedAt = at
    this.endedAt = null
    this.samples.length = 0
    this.reason = null
  }

  get recording(): boolean {
    return this.startedAt !== null && this.endedAt === null
  }

  push(samples: readonly Sample[]): void {
    if (!this.recording) return
    for (const s of samples) this.samples.push(s)
  }

  get rows(): number {
    return this.samples.length
  }

  /** `finish_session` for a clean end; anything else marks an aborted attempt. */
  stop(reason: string, at: Instant = instantNow()): void {
    if (this.startedAt === null || this.endedAt !== null) return
    this.endedAt = at
    this.reason = reason
  }

  snapshot(): Recording | null {
    if (this.startedAt === null || this.endedAt === null) return null
    return {
      startedAt: this.startedAt,
      endedAt: this.endedAt,
      channels: this.channels,
      samples: this.samples.slice(),
      finalizeReason: this.reason ?? 'unknown',
      partial: false,
    }
  }
}

/* ── meta.json ──────────────────────────────────────────────────────────── */

export type JournalStatus = 'answered' | 'skipped' | 'unanswered'

/**
 * The three `meta.json` fields that link the two recordings of a paired session
 * (Sprint 1.9.13 W2; backend B1 reads the same three from the ingest request).
 * All three or none: a plain recording carries none of them.
 */
export interface PairMeta {
  pair_id: string
  pair_role: 'EO' | 'EC'
  pair_index: 1 | 2
}

/** A UUID v4 for `pair_id`, minted when the paired attempt starts. */
export function mintPairId(random: (bytes: Uint8Array) => Uint8Array = (b) => crypto.getRandomValues(b)): string {
  const b = random(new Uint8Array(16))
  b[6] = (b[6]! & 0x0f) | 0x40 // version 4
  b[8] = (b[8]! & 0x3f) | 0x80 // RFC 4122 variant
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export interface Meta {
  session_id: string
  app: { name: string; version: string }
  start_wallclock_ns: string
  end_wallclock_ns: string | null
  finalize_reason: string
  partial_session: boolean
  start_wallclock_iso: string
  end_wallclock_iso: string | null
  sampling_rate: number
  channel_names: string[]
  hardware_channel_names: string[]
  derived_channel_names: string[]
  eog_source: string[]
  device_info: {
    name: string
    serial_number: string | null
    sens_family: string
    battery: number | null
    family: string
    contact_quality: 'measured' | 'estimated_from_signal'
  }
  samples_received_total: number
  session_mode: 'record'
  journal_status: JournalStatus
  /** Present, all three, only on a block of a paired session (`PairMeta`). */
  pair_id?: string
  pair_role?: 'EO' | 'EC'
  pair_index?: 1 | 2
}

export function eogSourceFor(channels: readonly string[]): string[] {
  if (channels.includes('Fp1') && channels.includes('Fp2')) return ['Fp1', 'Fp2']
  if (channels.includes('T3') && channels.includes('T4')) return ['T3', 'T4']
  return channels.slice(0, 2)
}

export function buildMeta(rec: Recording, device: HeadbandInfo, journalStatus: JournalStatus, appVersion: string, pair: PairMeta | null = null): Meta {
  const sessionId = mintSessionId(rec.startedAt)
  return {
    // The pair link first in the object, absent entirely from a plain recording:
    // `pair_id: null` would be a fourth state the backend's "all or none" refuses.
    ...(pair ? { pair_id: pair.pair_id, pair_role: pair.pair_role, pair_index: pair.pair_index } : {}),
    session_id: sessionId,
    app: { name: 'brainspot-web', version: appVersion },
    start_wallclock_ns: (BigInt(rec.startedAt.us) * 1000n).toString(),
    end_wallclock_ns: (BigInt(rec.endedAt.us) * 1000n).toString(),
    finalize_reason: rec.finalizeReason,
    partial_session: rec.partial,
    start_wallclock_iso: isoWithOffset(rec.startedAt),
    end_wallclock_iso: isoWithOffset(rec.endedAt),
    sampling_rate: device.samplingRate,
    channel_names: [...rec.channels, 'EOG'],
    hardware_channel_names: [...rec.channels],
    derived_channel_names: ['EOG'],
    eog_source: eogSourceFor(rec.channels),
    device_info: {
      name: device.name,
      serial_number: device.serial,
      sens_family: device.sensFamily,
      battery: device.battery,
      family: device.family,
      contact_quality: device.estimatedQuality ? 'estimated_from_signal' : 'measured',
    },
    samples_received_total: rec.samples.length,
    session_mode: 'record',
    journal_status: journalStatus,
  }
}

/* ── signal.parquet ─────────────────────────────────────────────────────── */

export function buildSignalParquet(rec: Recording, samplingRate = EXPECTED_SAMPLING_RATE): Uint8Array {
  const n = rec.samples.length
  const packNum = new BigInt64Array(n)
  const wallclock = new BigInt64Array(n)
  const perChannel = rec.channels.map(() => new Float32Array(n))
  const eog = new Float32Array(n)
  const eogIdx = eogSourceFor(rec.channels).map((ch) => rec.channels.indexOf(ch)).filter((i) => i >= 0)
  const startNs = BigInt(rec.startedAt.us) * 1000n
  const stepNs = BigInt(Math.round(1e9 / samplingRate))
  for (let i = 0; i < n; i++) {
    const s = rec.samples[i]!
    packNum[i] = BigInt(s.pack)
    wallclock[i] = startNs + stepNs * BigInt(i)
    let acc = 0
    for (let c = 0; c < rec.channels.length; c++) perChannel[c]![i] = s.values[c] ?? 0
    for (const idx of eogIdx) acc += s.values[idx] ?? 0
    eog[i] = eogIdx.length ? acc / eogIdx.length : 0
  }
  const buffer = parquetWriteBuffer({
    columnData: [
      { name: 'pack_num', data: packNum, type: 'INT64', nullable: false },
      { name: 'wallclock_ns', data: wallclock, type: 'INT64', nullable: false },
      ...rec.channels.map((ch, c) => ({ name: `${ch}_uV`, data: perChannel[c]!, type: 'FLOAT' as const, nullable: false })),
      { name: 'EOG_uV', data: eog, type: 'FLOAT', nullable: false },
    ],
    // One row group: 180 s is 45,000 rows, under MAX_SIGNAL_ROWS, and the backend caps groups at 256.
    rowGroupSize: MAX_SIGNAL_ROWS,
    // Column statistics are meaningless for a signal and cost footer bytes.
    statistics: false,
  })
  return new Uint8Array(buffer)
}

/* ── tar + gzip ─────────────────────────────────────────────────────────── */

export interface Member {
  name: string
  bytes: Uint8Array
}

function octal(n: number, width: number): Uint8Array {
  const s = n.toString(8).padStart(width - 1, '0') + '\0'
  return new TextEncoder().encode(s)
}

/** A plain ustar archive: regular files at the root, uid/gid 0, no names. */
export function buildTar(members: readonly Member[], mtimeSeconds: number): Uint8Array {
  const enc = new TextEncoder()
  const blocks: Uint8Array[] = []
  for (const m of members) {
    const h = new Uint8Array(512)
    h.set(enc.encode(m.name).subarray(0, 100), 0)
    h.set(octal(0o644, 8), 100)
    h.set(octal(0, 8), 108)
    h.set(octal(0, 8), 116)
    h.set(octal(m.bytes.length, 12), 124)
    h.set(octal(mtimeSeconds, 12), 136)
    h.fill(0x20, 148, 156) // checksum placeholder: spaces
    h[156] = 0x30 // '0' regular file
    h.set(enc.encode('ustar\0'), 257)
    h.set(enc.encode('00'), 263)
    let sum = 0
    for (const b of h) sum += b
    const chk = enc.encode(sum.toString(8).padStart(6, '0') + '\0 ')
    h.set(chk, 148)
    blocks.push(h, m.bytes)
    const pad = (512 - (m.bytes.length % 512)) % 512
    if (pad) blocks.push(new Uint8Array(pad))
  }
  blocks.push(new Uint8Array(1024))
  const total = blocks.reduce((n, b) => n + b.length, 0)
  const out = new Uint8Array(total)
  let off = 0
  for (const b of blocks) {
    out.set(b, off)
    off += b.length
  }
  return out
}

export interface TarEntry {
  name: string
  size: number
  typeflag: string
  bytes: Uint8Array
}

/** Walk a tar and return its regular-file entries. Enough to self-check our own output. */
export function readTar(tar: Uint8Array): TarEntry[] {
  const dec = new TextDecoder()
  const entries: TarEntry[] = []
  let off = 0
  while (off + 512 <= tar.length) {
    const h = tar.subarray(off, off + 512)
    if (h.every((b) => b === 0)) break
    const name = dec.decode(h.subarray(0, 100)).replace(/\0.*$/, '')
    const size = parseInt(dec.decode(h.subarray(124, 136)).replace(/\0.*$/, '').trim() || '0', 8)
    const typeflag = String.fromCharCode(h[156]!)
    const start = off + 512
    entries.push({ name, size, typeflag, bytes: tar.subarray(start, start + size) })
    off = start + Math.ceil(size / 512) * 512
  }
  return entries
}

async function pipeThrough(bytes: Uint8Array, stream: CompressionStream | DecompressionStream): Promise<Uint8Array> {
  const src = new Blob([bytes as BlobPart]).stream().pipeThrough(stream as unknown as ReadableWritablePair<Uint8Array, Uint8Array>)
  const buf = await new Response(src).arrayBuffer()
  return new Uint8Array(buf)
}

export const gzip = (bytes: Uint8Array) => pipeThrough(bytes, new CompressionStream('gzip'))
export const gunzip = (bytes: Uint8Array) => pipeThrough(bytes, new DecompressionStream('gzip'))

/* ── inspection: the refusals ───────────────────────────────────────────── */

export interface InspectInput {
  meta: Meta
  signalRows: number
  signalColumns: readonly string[]
  approxSeconds: number
}

export interface Inspection {
  refusals: Refusal[]
  layoutId: string | null
  layoutLabel: string | null
  eegChannels: string[]
}

function parseIsoNaive(iso: string): { naiveMs: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?([+-]\d{2}:\d{2}|Z)$/.exec(iso)
  if (!m) return null
  const frac = (m[7] ?? '').padEnd(6, '0')
  const naiveMs = Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!, +m[6]!) + Math.floor(+frac / 1000)
  return { naiveMs }
}

/** Every reason the recording could not be shipped, in one list. Never throws. */
export function inspect(input: InspectInput): Inspection {
  const refusals: Refusal[] = []
  const refuse = (code: RefusalCode, message: string) => refusals.push({ code, message })
  const { meta } = input

  // Session id: canonical shape, then agreeing with the start stamp within 1 s
  // on the NAIVE reading of both, exactly as the backend compares them.
  const idMatch = CANONICAL_SESSION_ID.exec(meta.session_id)
  if (!idMatch) {
    refuse('SESSION_ID_NOT_CANONICAL', 'the session id must be session_YYYY-MM-DDTHH-MM-SS-ffffff; session ordering depends on it')
  } else {
    const stampedMs = Date.UTC(+idMatch[1]!, +idMatch[2]! - 1, +idMatch[3]!, +idMatch[4]!, +idMatch[5]!, +idMatch[6]!) + Math.floor(+idMatch[7]! / 1000)
    const start = parseIsoNaive(meta.start_wallclock_iso)
    if (start === null) {
      refuse('SESSION_ID_NOT_CANONICAL', 'the start timestamp is not a timezone-aware ISO-8601 timestamp')
    } else {
      const drift = Math.abs(stampedMs - start.naiveMs) / 1000
      if (drift > MAX_ACQUISITION_DRIFT_S) {
        refuse('ID_TIMESTAMP_DISAGREE', `the session id and the start timestamp disagree by ${drift.toFixed(3)} s; the id must name the acquisition it describes`)
      }
    }
  }

  if (!Number.isFinite(meta.sampling_rate) || meta.sampling_rate !== EXPECTED_SAMPLING_RATE) {
    refuse('META_BAD_SAMPLING_RATE', `the sampling rate is ${meta.sampling_rate}; the backend expects ${EXPECTED_SAMPLING_RATE} Hz`)
  }
  if (meta.end_wallclock_ns === null) refuse('NEVER_ENDED', 'the session never ended')
  if (meta.partial_session) refuse('PARTIAL', 'the session is a checkpoint, not a finished recording')
  if (meta.finalize_reason !== CLEAN_FINALIZE_REASON) {
    refuse('NOT_FINISHED_CLEANLY', `the recording ended with ${meta.finalize_reason}; only a completed minute is processed`)
  }

  if (input.signalRows === 0) {
    refuse('NO_SIGNAL_ROWS', 'the recording holds no samples; there is nothing to score')
  } else if (input.signalRows > MAX_SIGNAL_ROWS) {
    refuse(
      'TOO_MANY_ROWS',
      `the recording holds ${input.signalRows.toLocaleString()} rows; the limit is ${MAX_SIGNAL_ROWS.toLocaleString()} (about ${input.approxSeconds.toFixed(1)} s of ${(MAX_SIGNAL_ROWS / EXPECTED_SAMPLING_RATE).toFixed(0)} s)`,
    )
  } else if (input.approxSeconds < MIN_COMPLETE_SECONDS - 0.5) {
    refuse('INCOMPLETE', `${input.approxSeconds.toFixed(1)} s were captured of the ${MIN_COMPLETE_SECONDS} s minute; an incomplete attempt is kept, not processed`)
  }

  const stems = input.signalColumns.filter((c) => c.endsWith('_uV') && c !== 'EOG_uV').map((c) => c.slice(0, -3))
  const eegChannels = stems.map((s) => CHANNEL_RENAME[s] ?? s).sort()
  let layoutId: string | null = null
  for (const [lid, chs] of Object.entries(KNOWN_LAYOUTS)) {
    const want = [...chs].sort()
    if (want.length === eegChannels.length && want.every((c, i) => c === eegChannels[i])) {
      layoutId = lid
      break
    }
  }
  if (layoutId === null) {
    refuse('UNKNOWN_LAYOUT', `the channel set {${eegChannels.join(', ')}} matches no BrainBit montage the backend knows`)
  }

  return { refusals, layoutId, layoutLabel: layoutId ? LAYOUT_LABELS[layoutId]! : null, eegChannels }
}

/* ── the builder ────────────────────────────────────────────────────────── */

export interface BuiltArchive {
  report: FinishReport
  /** The gzip'd tar, or null when refused. */
  file: File | null
  meta: Meta
  members: { name: string; bytes: number }[]
}

export interface BuildOptions {
  device: HeadbandInfo
  /** The `journal.json` payload, or null when skipped. */
  journal: object | null
  /**
   * Overrides the status derived from `journal` (answered / skipped). The
   * eyes-open block of a pair passes `'unanswered'`: the journal belongs to the
   * pair and travels with the eyes-closed block, so it was neither answered nor
   * skipped here.
   */
  journalStatus?: JournalStatus
  /** The pair link of a paired session's block; null or absent on a plain recording. */
  pair?: PairMeta | null
  appVersion?: string
  /** Injectable for tests. */
  now?: () => number
}

/**
 * Build meta + signal (+ journal), inspect, pack, reopen and prove. Returns the
 * report the phase machine's `finished` event carries; `file` is null on any
 * refusal.
 */
export async function buildArchive(rec: Recording, opts: BuildOptions): Promise<BuiltArchive> {
  const journalStatus: JournalStatus = opts.journalStatus ?? (opts.journal ? 'answered' : 'skipped')
  const meta = buildMeta(rec, opts.device, journalStatus, opts.appVersion ?? '0.0.0', opts.pair ?? null)
  const enc = new TextEncoder()
  const metaBytes = enc.encode(JSON.stringify(meta, null, 2) + '\n')
  const signalBytes = buildSignalParquet(rec, opts.device.samplingRate)
  const signalColumns = ['pack_num', 'wallclock_ns', ...rec.channels.map((c) => `${c}_uV`), 'EOG_uV']
  const rows = rec.samples.length
  const approxSeconds = rows / EXPECTED_SAMPLING_RATE
  const inspection = inspect({ meta, signalRows: rows, signalColumns, approxSeconds })

  const members: Member[] = [
    { name: 'meta.json', bytes: metaBytes },
    { name: 'signal.parquet', bytes: signalBytes },
  ]
  if (opts.journal) members.push({ name: 'journal.json', bytes: enc.encode(JSON.stringify(opts.journal, null, 2) + '\n') })

  const base: FinishReport = {
    sessionId: meta.session_id,
    rows,
    approxSeconds,
    layoutId: inspection.layoutId,
    layoutLabel: inspection.layoutLabel,
    journalIncluded: Boolean(opts.journal),
    bytes: 0,
    refusals: inspection.refusals,
  }
  const sizes = members.map((m) => ({ name: m.name, bytes: m.bytes.length }))
  if (inspection.refusals.length) return { report: base, file: null, meta, members: sizes }

  const mtime = Math.floor((opts.now ?? Date.now)() / 1000)
  const tar = buildTar(members, mtime)
  const gz = await gzip(tar)

  const selfCheck = await proveArchive(gz, members, rows)
  if (selfCheck) {
    return { report: { ...base, bytes: gz.length, refusals: [selfCheck] }, file: null, meta, members: sizes }
  }
  if (gz.length > MAX_ARCHIVE_BYTES) {
    return {
      report: { ...base, bytes: gz.length, refusals: [{ code: 'ARCHIVE_TOO_LARGE', message: `the archive is ${gz.length.toLocaleString()} bytes; the backend accepts at most ${MAX_ARCHIVE_BYTES.toLocaleString()}` }] },
      file: null,
      meta,
      members: sizes,
    }
  }
  const file = new File([gz as BlobPart], `${meta.session_id}.tar.gz`, { type: 'application/gzip', lastModified: mtime * 1000 })
  return { report: { ...base, bytes: gz.length }, file, meta, members: sizes }
}

/** Reopen the archive and prove it says what the builder says. Null when it does. */
export async function proveArchive(gz: Uint8Array, members: readonly Member[], rows: number): Promise<Refusal | null> {
  const fail = (message: string): Refusal => ({ code: 'SELF_CHECK_FAILED', message })
  let tar: Uint8Array
  try {
    tar = await gunzip(gz)
  } catch (err) {
    return fail(`the packed archive does not decompress: ${(err as Error).message}`)
  }
  const entries = readTar(tar)
  const names = entries.map((e) => e.name)
  const expected = members.map((m) => m.name)
  if (names.length !== expected.length || names.some((n, i) => n !== expected[i])) {
    return fail(`archive member list differs from plan: expected ${expected.join(', ')}, found ${names.join(', ')}`)
  }
  for (const e of entries) {
    if (e.typeflag !== '0') return fail(`archive member ${e.name} is not a regular file`)
    const planned = members.find((m) => m.name === e.name)!
    if (e.size !== planned.bytes.length) return fail(`archive member ${e.name} is ${e.size} bytes; it was written as ${planned.bytes.length}`)
    if (e.name !== 'signal.parquet') {
      const a = e.bytes
      const b = planned.bytes
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return fail(`archive member ${e.name} does not match the bytes written`)
    }
  }
  const signal = entries.find((e) => e.name === 'signal.parquet')!
  try {
    const copy = new Uint8Array(signal.bytes).buffer
    const md = parquetMetadata(copy)
    const numRows = Number(md.num_rows)
    if (numRows !== rows) return fail(`packed signal.parquet holds ${numRows} rows; ${rows} were recorded`)
    const leaves = md.schema.filter((s) => s.type !== undefined).map((s) => s.name)
    if (!leaves.includes('pack_num') || !leaves.includes('EOG_uV')) return fail('packed signal.parquet is missing pack_num or EOG_uV')
  } catch (err) {
    return fail(`packed signal.parquet footer is unreadable: ${(err as Error).message}`)
  }
  return null
}
